// LIBRERIE/IMPORTS
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';

// HOOKS
import { useGetAgencyDetails } from '../../hooks/useGetAgencyDetails';
import { useGetAgentsByAgency } from "../../hooks/useGetAgents";

// COMPONENTS
import WidgetContacts from '../Widgets/WidgetContacts/WidgetContacts';
import { WidgetMail } from '../Widgets/WidgetMail/WidgetMail';
import SubHeaderDefault from '../Header/subHeader/SubHeaderDefault';
import ServerError from '../Error/ServerError';
import WidgetPartners from '../Widgets/WidgetPartners/WidgetPartners';
import LoadingIndicator from '../Loaders/LoadingIndicator';
import AgentsSection from "../AboutUs/AgentsSection";
import MapLoader from '../Wrappers/GoogleMapsWrapper';

// CONFIGURAZIONI
import { appConfig } from "../../config/appConfig";

const showMap = appConfig.ui.contactUsPage.showMap;
  
interface Props {
    contactForm: boolean;
    widgetPartners: boolean;
    agencyId: number;
    showAgents: boolean;
}

interface MarkerInfo {
    name: string;
    adr: string;
    lat: number;
    lng: number;
}

const Contacts: React.FC<Props> = ({
    contactForm,
    widgetPartners,
    agencyId,
    showAgents,
}) => {
    const { t, ready } = useTranslation();
    const { agency, loading: agencyLoading, error: agencyError } = useGetAgencyDetails(agencyId);

    const [markerInfo, setMarkerInfo] = useState<MarkerInfo | null>(null);
    const { agents, loading: agentsLoading, error: agentsError } = useGetAgentsByAgency(agencyId);

    const staticPartnerUrl = agencyId ? `http://www.casain24ore.it/fromsito.php?agReg=${agencyId}` : 'http://www.casain24ore.it';
    const staticPartner = { url: staticPartnerUrl, data: { imgSrc: '/assets/images/c24.png', imgAlt: 'Network Immobiliare' } };
    const rawDynamicPartnersData = appConfig.content.partners;
    const validDynamicPartnersData = rawDynamicPartnersData.filter(p => p && p.imgSrc && p.imgSrc.trim() !== '');
    const fallbackPartnerAlt = t('widgets.partners.partnerLogoAltFallback');
    const formattedDynamicPartners = validDynamicPartnersData.map(p => ({
        url: p.url || '#',
        data: { imgSrc: p.imgSrc as string, imgAlt: p.imgAlt || fallbackPartnerAlt }
    }));
    const allPartnersForWidget = [staticPartner, ...formattedDynamicPartners];

    useEffect(() => {
        if (agency) {
            const addressParts = [
                agency.address_details?.address,
                agency.address_details?.city_name,
                agency.address_details?.sigla ? `(${agency.address_details.sigla})` : '',
            ].filter(Boolean);
            const fullAddress = addressParts.join(', ');


            setMarkerInfo({
                name: appConfig.map.markerData?.name || t('contactPage.map.markerDefaultName'),
                adr: fullAddress,
                lat: Number(appConfig.map.markerData?.lat),
                lng: Number(appConfig.map.markerData?.lng),
            });
        } else {
            setMarkerInfo(null);
        }
    }, [agency, t]);

    // --- Rendering Logic ---

    if (agencyLoading) return <LoadingIndicator />;
    if (agencyError) return <ServerError message={`${String(agencyError) || t('contactPage.errors.agencyLoadFailed')}`} />;
    if (agentsLoading && showAgents) return <LoadingIndicator />;
    if (agentsError && showAgents) console.error("Error fetching agents:", agentsError);

     if (!ready) return null;

    return (
        <>
            <Helmet>
                <title>{t('contactPage.pageTitle')} | {agency?.agencyName || 'Agenzia Immobiliare'}</title>
            </Helmet>
            <SubHeaderDefault title={t('contactPage.pageTitle')} isSeo={false}/>

            <section className="space-ptb contacts">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-12">
                            <div className="section-title">
                                <h3 className="asH2SubTitle">{t('contactPage.whereAreWe')}</h3>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-lg-12">
                            {showMap && <MapLoader markerInfo={markerInfo} />}
                        </div>

                        <div className="col-lg-8">
                            <div className="form-row">
                                {contactForm &&
                                    <WidgetMail />
                                }
                            </div>

                            {showAgents && agentsError && (
                                <div className="alert alert-danger">{t('aboutUsPage.agentsError', 'Errore nel caricamento degli agenti.')}</div>
                            )}
                            <AgentsSection
                                showAgents={showAgents}
                                agents={agents}
                                agentsLoading={agentsLoading}
                                agentsError={agentsError}
                            />
                        </div>

                        {/* Colonna Sidebar (Widgets) */}
                        <div className="col-lg-4 mt-4 mt-lg-0">
                            <div className="sidebar">
                                <WidgetContacts
                                    agencyDataAddress={agency?.address_details?.address ?? ''}
                                    agencyDataCity={agency?.address_details?.city_name ?? ''}
                                    agencyDataComuneCapoluogo={agency?.address_details?.city_name === agency?.address_details?.province_name}
                                    agencyDataProvinciaSigla={agency?.address_details?.sigla ?? ''}
                                    agencyDataEmail={appConfig.agency.agencyEmail}
                                    agencyDataEmailShow={appConfig.ui.agencyWidget.showEmail}
                                    agencyDataPhone1={agency?.phone1 ?? ''}
                                    agencyDataPhone2={agency?.phone2 ?? ''}
                                    agencyDataInternationalPrefix={appConfig.agency.intlPrefix ?? '+39'}
                                    agencyDataMobile={agency?.mobile ?? ''}
                                    agencyDataMobile2={agency?.mobile2 ?? ''}
                                    agencyDataFax={agency?.fax ?? ''}
                                    agencySocialShow={appConfig.ui.agencyWidget.showSocialLinks}
                                    agencyDataWhatsapp={appConfig.socialLinks.whatsapp}
                                    agencyDataFacebook={appConfig.socialLinks.facebook}
                                    agencyDataInstagram={appConfig.socialLinks.instagram}
                                    agencyDataTwitter={appConfig.socialLinks.twitter}
                                    agencyDataLinkedin={appConfig.socialLinks.linkedin}
                                    agencyDataPinterest={appConfig.socialLinks.pinterest}
                                    agencyDataYoutube={appConfig.socialLinks.youtube}
                                    agencyDataTikTok={appConfig.socialLinks.tiktok}
                                    agencyDataThreads={appConfig.socialLinks.threads}
                                />
                                {widgetPartners &&
                                    <WidgetPartners partners={allPartnersForWidget} />
                                }
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default Contacts;